import { Link } from "@/lib/nav";
import { ReactNode, useLayoutEffect, useRef } from "react";

import { cn } from "@/lib/utils";

export interface LinkTabItem {
  label: ReactNode;
  href: string;
}

interface Props {
  items: LinkTabItem[];
  activeHref: string;
  "aria-label": string;
  className?: string;
}

// 가로로 밀리는 탭 줄에서 현재 탭이 보이려면 얼마나 밀어야 하나 — 이미 보이면 0(세로 스크롤은 건드리지 않는다).
export const tabScrollDelta = (bar: { left: number; right: number }, tab: { left: number; right: number }): number => {
  if (tab.left < bar.left) return tab.left - bar.left;
  if (tab.right > bar.right) return Math.min(tab.right - bar.right, tab.left - bar.left);
  return 0;
};

// 화면을 바꾸는 밑줄 탭 — 목록을 거르는 FilterChips 와 모양을 가른다. 탭마다 Link 라 URL 이 상태다.
const LinkTabs = ({ items, activeHref, "aria-label": ariaLabel, className }: Props) => {
  const bar = useRef<HTMLElement>(null);

  // 좁은 화면에서 뒤쪽 탭으로 들어오면 그 탭이 가로 스크롤 밖에 숨는다 — 칠하기 전에 밀어 탭 줄이 튀지 않게 한다.
  useLayoutEffect(() => {
    const nav = bar.current;
    const tab = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !tab) return;
    const delta = tabScrollDelta(nav.getBoundingClientRect(), tab.getBoundingClientRect());
    if (delta !== 0) nav.scrollLeft += delta;
  }, [activeHref]);

  return (
    <nav ref={bar} aria-label={ariaLabel} className={cn("flex overflow-x-auto border-b border-border", className)}>
      {items.map((item) => {
        const active = item.href === activeHref;
        return (
          <Link
            key={item.href}
            aria-current={active ? "page" : undefined}
            className={cn(
              "flex h-10 shrink-0 items-center whitespace-nowrap border-b-2 px-3 text-sm",
              active
                ? "border-primary font-semibold text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
            href={item.href}>
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
};

export default LinkTabs;
